import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import {
  checkAdmin,
  formatCents,
  getAdminCashouts,
  getAdminSummary,
  getAdminUsers,
  markCashoutPaid,
  rejectCashout,
  type AdminCashout,
  type AdminSummary,
  type AdminUser,
} from '../lib/payments'
import { Icon, Spinner } from '../components/Icon'
import { Logo } from '../components/Logo'
import { Button, Panel, TextField, cx } from '../components/ui'
import { StatusPill } from './WalletScreen'
import { AuthScreen } from './AuthScreen'

type Tab = 'cashouts' | 'users'

export function AdminScreen() {
  const { user, initialising, logout } = useAuth()
  const [allowed, setAllowed] = useState<boolean | null>(null)
  const [tab, setTab] = useState<Tab>('cashouts')

  const [summary, setSummary] = useState<AdminSummary | null>(null)
  const [cashouts, setCashouts] = useState<AdminCashout[]>([])
  const [users, setUsers] = useState<AdminUser[]>([])
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showSettled, setShowSettled] = useState(false)

  const refresh = useCallback(async () => {
    const [s, c, u] = await Promise.all([
      getAdminSummary(),
      getAdminCashouts(showSettled ? undefined : 'requested'),
      getAdminUsers(query || undefined),
    ])
    setSummary(s)
    setCashouts(c.cashouts)
    setUsers(u.users)
  }, [query, showSettled])

  useEffect(() => {
    if (!user) {
      setAllowed(null)
      return
    }
    checkAdmin()
      .then((r) => setAllowed(r.isAdmin))
      .catch(() => setAllowed(false))
  }, [user])

  useEffect(() => {
    if (allowed !== true) return
    refresh().catch((caught) =>
      setError(caught instanceof Error ? caught.message : 'Could not load admin data.'),
    )
  }, [allowed, refresh])

  if (initialising) return <Centered><Spinner size={22} className="text-fg-dim" /></Centered>

  // Signing in happens through the ordinary screen: admin is an allowlist on a
  // normal account, not a separate credential.
  if (!user) return <AuthScreen />

  if (allowed === null) return <Centered><Spinner size={22} className="text-fg-dim" /></Centered>

  if (allowed === false) {
    return (
      <Centered>
        <div className="max-w-sm text-center">
          <p className="display-title text-sm text-fg">Not found</p>
          <p className="mt-2 text-sm text-fg-dim">
            This page does not exist for your account.
          </p>
          <Button size="sm" variant="ghost" className="mt-6" onClick={() => (window.location.href = '/')}>
            Back to the game
          </Button>
        </div>
      </Centered>
    )
  }

  const settle = async (id: string, action: 'paid' | 'reject') => {
    setError(null)
    setBusy(id)
    try {
      if (action === 'paid') await markCashoutPaid(id)
      else await rejectCashout(id, 'Rejected from admin')
      await refresh()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not update that request.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="min-h-dvh pad-safe pad-safe-top">
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 py-4">
        <div className="flex items-center gap-3">
          <Logo size="sm" />
          <span className="eyebrow rounded-full border border-ink-700 px-2.5 py-1">Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden text-xs text-fg-dim sm:block">{user.email}</span>
          <button
            type="button"
            onClick={logout}
            aria-label="Sign out"
            className="flex h-11 w-11 items-center justify-center rounded-xl text-fg-dim transition-colors hover:bg-ink-850 hover:text-fg"
          >
            <Icon name="logout" size={18} />
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl pb-16">
        {/* Money at a glance. Player balance is what the business owes. */}
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Taken in" value={formatCents(summary?.chargedCents ?? 0)} tone="lime" />
          <Stat label="Paid out" value={formatCents(summary?.paidOutCents ?? 0)} />
          <Stat label="Fees kept" value={formatCents(summary?.feesCollectedCents ?? 0)} tone="gold" />
          <Stat
            label="Owed to players"
            value={formatCents(summary?.playerBalanceCents ?? 0)}
            hint={`${summary?.players ?? 0} accounts`}
          />
        </div>

        {(summary?.pendingCashoutCount ?? 0) > 0 && (
          <div className="mb-5 flex items-center gap-2.5 rounded-xl border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-gold">
            <Icon name="clock" size={16} />
            {summary?.pendingCashoutCount} cashout
            {(summary?.pendingCashoutCount ?? 0) === 1 ? '' : 's'} awaiting payment,
            totalling {formatCents(summary?.pendingCashoutCents ?? 0)}.
          </div>
        )}

        {error && (
          <p role="alert" className="mb-4 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            {error}
          </p>
        )}

        <div className="mb-4 flex items-center gap-2">
          <TabButton active={tab === 'cashouts'} onClick={() => setTab('cashouts')}>
            Cashouts
          </TabButton>
          <TabButton active={tab === 'users'} onClick={() => setTab('users')}>
            Players
          </TabButton>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => void refresh()}>
            <Icon name="refresh" size={16} />
            Refresh
          </Button>
        </div>

        {tab === 'cashouts' ? (
          <>
            <label className="mb-3 flex cursor-pointer items-center gap-2 text-xs text-fg-dim">
              <input
                type="checkbox"
                checked={showSettled}
                onChange={(e) => setShowSettled(e.target.checked)}
                className="h-4 w-4 accent-lime"
              />
              Include settled requests
            </label>

            {cashouts.length === 0 ? (
              <Panel flush className="p-8 text-center">
                <p className="text-sm text-fg-muted">Nothing waiting. All caught up.</p>
              </Panel>
            ) : (
              <div className="space-y-2">
                {cashouts.map((row) => {
                  // Flagged when the request exceeds what the account actually
                  // holds - it should be impossible, so it is worth seeing.
                  const underfunded = (row.cashCents ?? 0) < row.grossCents
                  return (
                    <Panel key={row.id} flush className="p-4">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-fg">{row.displayName}</p>
                          <p className="mt-0.5 truncate text-xs text-fg-dim">{row.email}</p>
                          <p className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                            <span className="text-fg-muted">PayPal:</span>
                            <span className="font-mono text-fg">{row.paypalEmail}</span>
                          </p>
                          <p className="mt-1 text-xs text-fg-dim">
                            {new Date(row.createdAt).toLocaleString()}
                          </p>
                        </div>

                        <div className="text-right">
                          <p className="font-mono text-lg font-semibold tnum text-gold">
                            {formatCents(row.netCents)}
                          </p>
                          <p className="mt-0.5 font-mono text-[0.6875rem] tnum text-fg-dim">
                            gross {formatCents(row.grossCents)} · fee {formatCents(row.feeCents)}
                          </p>
                          <p className="mt-1 font-mono text-[0.6875rem] tnum text-fg-dim">
                            balance {formatCents(row.cashCents ?? 0)}
                          </p>
                        </div>
                      </div>

                      {underfunded && (
                        <p className="mt-3 flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
                          <Icon name="close" size={14} />
                          Request exceeds the account balance. Do not pay without checking.
                        </p>
                      )}

                      {row.status === 'requested' ? (
                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            loading={busy === row.id}
                            onClick={() => void settle(row.id, 'paid')}
                          >
                            <Icon name="check" size={16} />
                            Mark paid
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={busy === row.id}
                            onClick={() => void settle(row.id, 'reject')}
                          >
                            Reject
                          </Button>
                        </div>
                      ) : (
                        <div className="mt-3 flex items-center gap-2">
                          <StatusPill status={row.status} />
                          {row.settledAt && (
                            <span className="text-xs text-fg-dim">
                              {new Date(row.settledAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </Panel>
                  )
                })}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="mb-3">
              <TextField
                label="Search players"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Email or display name"
              />
            </div>

            <div className="overflow-x-auto rounded-2xl border border-ink-700">
              <table className="w-full min-w-[860px] text-sm">
                <thead>
                  <tr className="border-b border-ink-700 text-left">
                    <Th>Player</Th>
                    <Th align="right">Charged</Th>
                    <Th align="right">Won</Th>
                    <Th align="right">Withdrawn</Th>
                    <Th align="right">Cash</Th>
                    <Th align="right">Coins</Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((row) => (
                    <tr key={row.id} className="border-b border-ink-800 last:border-0">
                      <td className="px-4 py-3">
                        <p className="font-medium text-fg">{row.displayName}</p>
                        <p className="text-xs text-fg-dim">{row.email}</p>
                      </td>
                      <Td>{formatCents(row.chargedCents)}</Td>
                      <Td>{formatCents(row.earnedCents)}</Td>
                      <Td>{formatCents(row.withdrawnCents)}</Td>
                      <Td strong>{formatCents(row.cashCents)}</Td>
                      <Td muted>{row.coins.toLocaleString()}</Td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-sm text-fg-dim">
                        No players match that search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-dvh items-center justify-center px-6">{children}</div>
}

function Stat({
  label,
  value,
  tone,
  hint,
}: {
  label: string
  value: string
  tone?: 'lime' | 'gold'
  hint?: string
}) {
  return (
    <Panel flush className="p-4">
      <p className="eyebrow">{label}</p>
      <p
        className={cx(
          'mt-2 font-mono text-2xl font-semibold tnum',
          tone === 'lime' ? 'text-lime' : tone === 'gold' ? 'text-gold' : 'text-fg',
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-fg-dim">{hint}</p>}
    </Panel>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'h-11 rounded-xl px-4 text-sm font-semibold transition-colors',
        active ? 'bg-lime-wash text-lime' : 'text-fg-muted hover:bg-ink-850 hover:text-fg',
      )}
    >
      {children}
    </button>
  )
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={cx(
        'px-4 py-3 text-xs font-semibold uppercase tracking-wider text-fg-dim',
        align === 'right' && 'text-right',
      )}
    >
      {children}
    </th>
  )
}

function Td({
  children,
  strong,
  muted,
}: {
  children: React.ReactNode
  strong?: boolean
  muted?: boolean
}) {
  return (
    <td
      className={cx(
        'px-4 py-3 text-right font-mono text-sm tnum',
        strong ? 'font-semibold text-gold' : muted ? 'text-fg-dim' : 'text-fg-muted',
      )}
    >
      {children}
    </td>
  )
}
